import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Icon from './Icon';
import '../styles/components/LoginPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

type VerifyState = 'checking' | 'valid' | 'invalid';

const SetPasswordPage: React.FC = () => {
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token') || '');
  const [verifyState, setVerifyState] = useState<VerifyState>('checking');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifyState('invalid');
      return;
    }
    axios.get(`${API_BASE_URL}/auth/set-password/verify`, { params: { token } })
      .then(res => {
        setEmail(res.data.email);
        setVerifyState('valid');
      })
      .catch(() => setVerifyState('invalid'));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/set-password`, { token, password });
      setDone(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'This link is invalid or has expired.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">
            <Icon name="sparkles" size={40} />
          </div>
          <h1 className="login-title">Anojeje</h1>
          <p className="login-subtitle">Set your password</p>
        </div>

        <div className="login-divider" />

        {error && <div className="login-error">{error}</div>}

        {verifyState === 'checking' && (
          <p className="login-description">Checking your link…</p>
        )}

        {verifyState === 'invalid' && (
          <p className="login-description">
            This link is invalid or has expired. Ask an admin to resend your welcome email.
          </p>
        )}

        {verifyState === 'valid' && !done && (
          <form className="login-form" onSubmit={handleSubmit}>
            <p className="login-description login-description-flush">
              Setting a password for <strong>{email}</strong>.
            </p>
            <div className="login-field">
              <label className="login-label" htmlFor="set-password">New password</label>
              <input
                id="set-password"
                className="login-input"
                type="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="login-field">
              <label className="login-label" htmlFor="confirm-password">Confirm password</label>
              <input
                id="confirm-password"
                className="login-input"
                type="password"
                autoComplete="new-password"
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              className="login-submit-button"
              disabled={submitting || !password || !confirmPassword}
            >
              {submitting ? 'Setting password…' : 'Set Password'}
            </button>
          </form>
        )}

        {done && (
          <>
            <p className="login-description">Your password is set. You can now sign in.</p>
            <a className="login-submit-button login-submit-button-link" href="/">
              Go to Sign In
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default SetPasswordPage;
